import './LoadFromFileExecutor.scss';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { MdOutlineInsertDriveFile } from 'react-icons/md';
import { IFileData } from '@srm/ui/types/IFileData';
import { v4 as uuidv4 } from 'uuid';
import InputFile from '@srm/ui/input-file';
import { MAX_FILE_SIZE } from '@/constants';
import SelectableList from '@srm/ui/selectable-list';
import ModalContainer, { IModalContainerProps } from '@molecules/ModalContainer/ModalContainer';
import BackgroundJobWait from '@molecules/BackgroundJobWait/BackgroundJobWait';
import { backgroundJobModalProps } from '@molecules/BackgroundJobWait/backgroundJobWaitModal';
import { ILoadFromFileExecutorProps } from './ILoadFromFileExecutorProps';
import { createSyncLoadFromFileTransport, LoadFromFileTransport } from './loadFromFileTransport';
import { createBackgroundLoadFromFileTransport } from './backgroundLoadFromFileTransport';
import { useLoadFromFileFlow } from './useLoadFromFileFlow';
import { BackgroundJobAction, openJobLink } from '@services/backgroundJobs/backgroundJobKinds';

interface ISelectedFile {
    id: string;
    file: File;
}

export interface ILoadFromFileExecutorModalProps extends ILoadFromFileExecutorProps {
    /** Как довести загрузку до итога; по умолчанию — один синхронный запрос. */
    transport?: LoadFromFileTransport;
}

export const LoadFromFileExecutorModal: React.FC<ILoadFromFileExecutorModalProps> = (props) => {
    const { docId, actionKey, completeHandler, runInBackground = false } = props;
    const showResult = props.showResult || runInBackground;
    const [selected, setSelected] = useState<ISelectedFile[]>([]);
    const [returned, setReturned] = useState(false);
    /** Файл уже загрузился, а пользователь выбрал ещё один: окно закроется с успехом, что бы ни было дальше. */
    const succeededRef = useRef(false);
    const formMethods = useForm();
    const navigate = useNavigate();

    const transport = useMemo(
        () =>
            props.transport ??
            (runInBackground
                ? createBackgroundLoadFromFileTransport(docId, actionKey)
                : createSyncLoadFromFileTransport(docId, actionKey)),
        [props.transport, runInBackground, docId, actionKey],
    );
    const flow = useLoadFromFileFlow({
        transport,
        showResult,
        successText: props.successText,
        onComplete: completeHandler,
    });

    const setFile = (files: IFileData[]) => {
        if (!files) return;
        setSelected(files.flatMap((f) => (f.file ? [{ id: uuidv4(), file: f.file }] : [])));
    };

    const onRemoveFile = useCallback((fileId: string) => {
        setSelected((files) => files.filter((file) => file.id !== fileId));
    }, []);

    const fileItems = useMemo(
        () =>
            selected.map(({ id, file }) => ({
                key: id,
                title: file.name,
                icon: <MdOutlineInsertDriveFile size={18} />,
            })),
        [selected],
    );

    const pickAgain = () => {
        if (flow.result?.tone === 'ok') succeededRef.current = true;
        setSelected([]);
        setReturned(true);
        flow.reset();
    };

    const hasFile = selected.length > 0;
    const { phase, result } = flow;
    const close = (isSucceed: boolean) => completeHandler?.(isSucceed || succeededRef.current);
    // Действие итога — ссылка на папку с созданными документами: переход и закрытие окна с успехом.
    const onResultAction = (action: BackgroundJobAction) => {
        if (action.type !== 'openLink') return;
        close(true);
        openJobLink(action.href, (path) => void navigate(path));
    };

    const picker = (
        <>
            <InputFile
                className="LoadFromFile__SelectFileInput"
                onChange={setFile}
                maxSize={MAX_FILE_SIZE}
                multiple={false}
                showChips={false}
                clearFilesAfterSet={true}
                accept={props.allowedFileTypes ? props.allowedFileTypes.toString() : '*'}
            />

            <SelectableList
                mode="none"
                items={fileItems}
                selected={[]}
                onChange={() => {}}
                onRemove={onRemoveFile}
                removeTitle="Убрать файл"
                emptyText="Файл не выбран"
                ariaLabel="Выбранные файлы"
            />
        </>
    );

    const baseProps: IModalContainerProps = {
        header: props.title,
        size: props.modalSize,
        ...backgroundJobModalProps(flow, {
            title: props.jobTitle || props.title,
            close: () => close(false),
            cancelButtonText: props.cancelButtonText,
        }),
        // С итогом в окне кнопки действия, пока идёт загрузка, нет; без итога окно ждёт под оверлеем.
        showOkButton: !showResult || !flow.busy,
        showCancelButton: true,
        okButtonText: props.okButtonText,
        hasUnsavedInput: () => phase === 'idle' && hasFile,
        okButtonDisabled: !hasFile || flow.busy,
        errorText: flow.errorText,
        formMethods,
        onSubmit: () => {
            if (hasFile) flow.start(selected[0].file);
        },
    };

    const resultProps: Partial<IModalContainerProps> | undefined =
        phase !== 'result' || !result
            ? undefined
            : result.tone === 'ok'
              ? {
                    okButtonText: 'Готово',
                    okButtonDisabled: false,
                    onSubmit: () => close(true),
                    cancelButtonText: 'Загрузить ещё файл',
                    onCancelButtonClick: pickAgain,
                    onCancel: () => close(true),
                }
              : {
                    okButtonText: 'Выбрать другой файл',
                    okButtonDisabled: false,
                    onSubmit: pickAgain,
                    cancelButtonText: 'Закрыть',
                };

    if (!showResult) {
        return (
            <ModalContainer {...baseProps} loading={flow.busy}>
                {picker}
            </ModalContainer>
        );
    }

    return (
        <ModalContainer {...baseProps} {...resultProps}>
            <BackgroundJobWait
                ref={flow.waitRef}
                kind="loadFromFile"
                phase={phase}
                returned={returned}
                subject={
                    <>
                        Загружаем <b>{flow.fileName}</b>
                    </>
                }
                stageText={flow.stage}
                stageName={flow.stageName}
                background={flow.background}
                result={result}
                onDownload={flow.downloadResultFile}
                downloading={flow.downloadingResultFile}
                onAction={onResultAction}
            >
                {picker}
            </BackgroundJobWait>
        </ModalContainer>
    );
};
